<template>
  <div class="card-list">
    <dr-loader
      v-if="ui.isLoading"
      class="m-auto"
      size="lg"
    />

    <p
      v-if="!ui.isLoading && cards.length === 0"
      class="card-list__not-found"
    >
      Nenhum cartão cadastrado.
    </p>

    <ul
      v-if="!ui.isLoading && cards.length > 0"
      class="card-list__list"
      :class="{ 'cursor-not-allowed': ui.isRemoving }"
    >
      <li
        v-for="(card, index) in cards"
        :key="`card-item-${index}`"
        class="card-list__item"
        :class="{
          'pointer-events-none': ui.isRemoving,
          'card-list__item--default': card.isDefault
        }"
      >
        <label
          class="form-radio"
          :for="`card-${card.id}`"
        >
          <input
            v-model="selectedCard"
            :id="`card-${card.id}`"
            type="radio"
            :value="card.id"
            class="h-8"
          />
          <i class="form-icon" />
        </label>

        <img
          :src="getCardBrand(card.brand)"
          class="card-list__brand"
        />

        <p class="card-list__text">
          Final <strong>{{ card.lastDigits }}</strong>
        </p>

        <p class="card-list__text">
          Expira em <strong>{{ card.expirationDate | toExpDate }}</strong>
        </p>

        <div
          v-if="card.isDefault"
          class="label card-default"
        >
          Padrão
        </div>

        <button
          class="card-list__btn"
          @click="confirmRemove(card.id)"
        >
          <dr-icon
            fill="#EF3120"
            color="#EF3120"
            class="card-list__remove"
            name="excluiAlerta"
          />
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import visa from '@/assets/img/credit-card/visa.png'
import master from '@/assets/img/credit-card/mastercard.png'
import elo from '@/assets/img/credit-card/elo.png'
import american from '@/assets/img/credit-card/americanExpress.png'
import hipercard from '@/assets/img/credit-card/hipercard.png'
import genericBrand from '@/assets/img/credit-card/genericCard.png'
import EventBus from '@/globals/eventBus'

export default {
  name: 'PlansCreditCardList',

  props: {
    card: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },

  model: {
    prop: 'card',
    event: 'change'
  },

  filters: {
    toExpDate (str) {
      return str.replace(/(\d{2})(\d{2})/gi, '$1/$2')
    }
  },

  data () {
    return {
      ui: {
        isLoading: false,
        isRemoving: false
      },
      cards: [],
      selectedCard: null
    }
  },

  watch: {
    card: {
      deep: true,
      immediate: true,
      handler ({ id = null } = {}) {
        if (id) {
          this.selectedCard = id
          return
        }

        this.selectedCard = null
      }
    },

    selectedCard: {
      deep: false,
      immediate: false,
      handler () {
        this.$emit('change', this.cards.find((card) => card.id === this.selectedCard))
      }
    }
  },

  created () {
    this.getCards()

    EventBus.$on('reload-credit-card-list', this.getCards)
  },

  methods: {
    getCards () {
      const url = '/user/credit_card'

      this.ui.isLoading = true
      this.fn_SHOW_PROGRESS()
      this.$http
        .get(url, this.$store.getters.getAuthHeader)
        .then((res) => {
          this.cards = res.data.map((card) => {
            return {
              id: card.id,
              lastDigits: card['last_digits'],
              expirationDate: card['expiration_date'],
              brand: card.brand,
              isDefault: card['is_default']
            }
          })

          this.$emit('cards-number', this.cards.length)
        })
        .catch((err) => this.fn_HANDLE_ERROR(err))
        .finally(() => {
          this.ui.isLoading = false
          this.fn_HIDE_PROGRESS()
        })
    },

    getCardBrand (brand) {
      switch (true) {
        case brand === 'visa':
          return visa

        case brand === 'mastercard':
          return master

        case brand === 'elo':
          return elo

        case brand === 'amex':
          return american

        case brand === 'hipercard':
          return hipercard

        default:
          return genericBrand
      }
    },

    confirmRemove (id) {
      this.$store.dispatch('setModalConfirm', {
        title: 'Confirmação',
        body: `Deseja realmente remover o cartão?`,
        confirmText: 'Confirmar',
        cancelText: 'Cancelar',
        beforeClose: (v) => {
          if (v) {
            this.removeCard(id)
          }

          this.$store.dispatch('setModalConfirm')
        },
        size: 'md',
        active: true
      })
    },

    removeCard (id) {
      const url = `/user/credit_card/${id}`

      this.ui.isRemoving = true
      this.fn_SHOW_PROGRESS()

      this.$http
        .delete(url, this.$store.getters.getAuthHeader)
        .then(() => {
          this.$store.dispatch('setModalAlert', {
            title: 'Sucesso',
            body: 'Cartão removido com sucesso.',
            size: 'md',
            active: true
          })

          this.getCards()
          this.$emit('update')
        })
        .catch((err) => this.fn_HANDLE_ERROR(err))
        .finally(() => {
          this.ui.isRemoving = false
          this.fn_HIDE_PROGRESS()
        })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '$vars';

.card-list {
  &__not-found {
    @apply p-0 m-0;
    font-size: 0.75rem;
    box-sizing: border-box;
  }

  &__list {
    @apply flex flex-col items-start justify-start list-none w-full;
    @apply p-0 m-0;
    box-sizing: border-box;
  }

  &__item {
    @apply flex flex-row items-center justify-start w-full mb-2;
    @apply px-3 py-2 rounded rounded-lg;
    background: white;
    box-sizing: border-box;
    min-height: 3rem;

    &--default {
      box-shadow: 0 0 3px 1px rgba($color-navy, 0);
    }
  }

  &__brand {
    @apply mr-3;
    max-height: 2rem;
    max-width: 2.5rem;
    width: auto;
  }

  &__text {
    @apply m-0 p-0 mr-3;
  }

  &__btn {
    margin-left: auto;
  }

  &__remove {
    height: 1rem;
    width: 1rem;
  }
}

.card-default {
  background: white;
  border: 1px solid $color-navy;
  color: $color-navy;
}
</style>
